import { getSubdomain, fetchWithAuth } from "./helpers";
import { API_PASSWORD } from "./config";

export const createNewUser = async (userData) => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/create/new/user`;

  const formData = new FormData();
  formData.append("api_password", API_PASSWORD);
  formData.append("name", userData.name);
  formData.append("username", userData.username);
  formData.append("type", userData.type);
  formData.append("password", userData.password);

  const response = await fetchWithAuth(
    apiUrl,
    {
      method: "POST",
      body: formData,
    },
    true
  ); // formData => true

  const data = await response.json();

  if (!response.ok) {
    console.error("❌ API Error:", data);
    throw new Error(data.message || "❌ فشل في إنشاء المستخدم.");
  }

  return data;
};

export const getAllUsers = async () => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/get/users?api_password=${API_PASSWORD}`;

  const response = await fetchWithAuth(apiUrl, {
    method: "GET",
  });

  const data = await response.json();

  if (!response.ok) {
    console.error("❌ API Error:", data);
    throw new Error(data.message || "فشل في جلب بيانات المستخدمين");
  }

  return data.data?.data || []; // ✅ استخراج مصفوفة المستخدمين من الهيكل
};


export const updateUserData = async (userData) => {
  const subdomain = getSubdomain();
  // ✅ تأكدنا من إضافة api_password مرة واحدة فقط
  const apiUrl = `https://${subdomain}.restocash.app/api/update/user?api_password=${API_PASSWORD}`;

  const formData = new FormData();
  formData.append("id", userData.id);
  formData.append("name", userData.name);
  formData.append("username", userData.username);
  formData.append("type", userData.type);
  formData.append("password", userData.password ?? ""); // لتفادي undefined

  // ✅ إضافة status لو مطلوب
  if (userData.status !== undefined) {
    formData.append("status", userData.status);
  }

  // ✅ طباعة البيانات قبل الإرسال
  for (let pair of formData.entries()) {
    console.log(`${pair[0]}: ${pair[1]}`);
  }

  const response = await fetchWithAuth(
    apiUrl,
    {
      method: "POST",
      body: formData,
    },
    true
  );

  const text = await response.text();
  console.log("Response Text:", text);

  let data;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error("خطأ في تحليل استجابة السيرفر");
  }

  if (!response.ok) {
    let errorMessage = data.message || "فشل في تعديل المستخدم";
  
    if (data.errors) {
      const fieldErrors = [];
      for (const field in data.errors) {
        const fieldMessages = data.errors[field].join(", ");
        fieldErrors.push(`${field}: ${fieldMessages}`);
      }
      errorMessage += " => " + fieldErrors.join(" | ");
    }
  
    throw new Error(errorMessage);
  }
  

  return data;
};



export const addUserRoots = async (userId, rootIds) => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/add/user/roots`;

  const params = new URLSearchParams();
  params.append("api_password", API_PASSWORD);
  params.append("user_id", userId);

  rootIds.forEach(id => params.append("root_ids[]", id));

  const finalUrl = `${apiUrl}?${params.toString()}`;

  const response = await fetchWithAuth(
    finalUrl,
    {
      method: "POST",
    }
  );

  const text = await response.text();
  console.log("Response Text:", text);

  let data;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error("خطأ في تحليل استجابة السيرفر");
  }

  if (!response.ok) {
    let errorMessage = data.message || "فشل في ربط الفروع";
    if (data.errors) {
      const fieldErrors = [];
      for (const field in data.errors) {
        const fieldMessages = data.errors[field].join(", ");
        fieldErrors.push(`${field}: ${fieldMessages}`);
      }
      errorMessage += " => " + fieldErrors.join(" | ");
    }
    throw new Error(errorMessage);
  }

  return data;
};



export const removeUserRoots = async (userId, rootIds) => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/remove/user/roots`;

  const params = new URLSearchParams();
  params.append("api_password", API_PASSWORD);
  params.append("user_id", userId);

  rootIds.forEach(id => params.append("root_ids[]", id));

  const finalUrl = `${apiUrl}?${params.toString()}`;

  const response = await fetchWithAuth(
    finalUrl,
    {
      method: "POST",
    }
  );

  const text = await response.text();
  console.log("Response Text:", text);

  let data;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error("خطأ في تحليل استجابة السيرفر");
  }

  if (!response.ok) {
    let errorMessage = data.message || "فشل في حذف صلاحيات الفروع";
    if (data.errors) {
      const fieldErrors = [];
      for (const field in data.errors) {
        const fieldMessages = data.errors[field].join(", ");
        fieldErrors.push(`${field}: ${fieldMessages}`);
      }
      errorMessage += " => " + fieldErrors.join(" | ");
    }
    throw new Error(errorMessage);
  }

  return data;
};



export const setUserBankPermissions = async (userId, bankPermissions) => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/add/user/bank?api_password=${API_PASSWORD}`;

  const bodyData = {
    user_id: userId,
    bank_ids: bankPermissions.map(p => p.bank_id),
    permissions: bankPermissions.map(p => ({
      bank_id: p.bank_id,
      deposit_pay: p.deposit_pay ?? 0,
      deposit: p.deposit ?? 0,
      withdrawn: p.withdrawn ?? 0,
      move: p.move ?? 0,
      show: p.show ?? 0,
      hide_total: p.hide_total ?? 0,
    }))
  };

  const response = await fetchWithAuth(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(bodyData),
  });

  const text = await response.text();
  console.log("Response Text:", text);

  let data;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error("خطأ في تحليل استجابة السيرفر");
  }

  if (!response.ok) {
    throw new Error(data.message || "فشل في حفظ صلاحيات المستخدم على الخزن");
  }

  return data;
};
// عرض الفروع المتاحة للمتخدم عشان الصلاحييات
export const getUserById = async (userId) => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/get/users?api_password=${API_PASSWORD}`;

  const response = await fetchWithAuth(apiUrl, { method: "GET" });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "فشل في جلب المستخدمين");
  }

  const user = data.data?.data?.find((u) => u.id === userId);
  if (!user) throw new Error("لم يتم العثور على المستخدم");

  return user;
};



// صلاحيات الخزن
// services/Users.js
export const fetchUserBanks = async (userId) => {
  const subdomain = window.location.hostname.split('.')[0];
  const apiUrl = `https://${subdomain}.restocash.app/api/user/bank/${userId}?api_password=Project102$_`;

  const response = await fetch(apiUrl, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || "❌ فشل في تحميل صلاحيات الخزن");
  }

  return data.data;
};

// services/Users.js




export const fetchRootBanks = async () => {
  const subdomain = getSubdomain();
  const rootId = localStorage.getItem("branch_id");

  if (!rootId) {
    throw new Error("❌ branch_id غير موجود في localStorage");
  }

  const url = `https://${subdomain}.restocash.app/api/root/banks/${rootId}`;

  const response = await fetchWithAuth(url, { method: "GET" });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || "❌ فشل في تحميل بيانات الخزن");
  }

  return data.data;
};
// services/Users.js

export const removeUserBankPermissions = async (userId, bankIds) => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/remove/user/bank`;

  const bodyData = {
    user_id: userId,
    bank_ids: bankIds,
  };

  const response = await fetchWithAuth(apiUrl, {
    method: "POST",
    body: JSON.stringify(bodyData),
  });

  const text = await response.text();
  console.log("Response Text:", text);

  let data;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error("خطأ في تحليل استجابة السيرفر");
  }

  if (!response.ok) {
    throw new Error(data.message || "فشل في حذف صلاحيات الخزن للمستخدم");
  }

  return data;
};

// services/Users.js


export const updateUserBankPermissions = async (userId, bankId, permissions) => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/update/user/bank?api_password=${API_PASSWORD}`;

  const body = {
    user_id: userId,
    bank_id: bankId,
    permissions: {
      deposit_pay: permissions.deposit_pay ?? 0,
      deposit: permissions.deposit ?? 0,
      withdrawn: permissions.withdrawn ?? 0,
      move: permissions.move ?? 0,
      show: permissions.show ?? 0,
      hide_total: permissions.hide_total ?? 0,
    },
  };

  const response = await fetchWithAuth(apiUrl, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "فشل في تعديل صلاحيات الخزنة");
  }

  return data;
};



// ✅ إضافة مستخدم إلى كاشير - بصيغة form-data مثل Postman
export const addUserToCashier = async ({ user_id, cashier_id }) => {
  if (!user_id || !cashier_id) {
    throw new Error("❗ يجب توفير user_id و cashier_id");
  }

  const subdomain = getSubdomain();
  const url = `https://${subdomain}.restocash.app/api/add/user/cashier?api_password=${API_PASSWORD}`;

  const formData = new FormData();
  formData.append("cashier_id", cashier_id);
  formData.append("user_ids[]", user_id); // ⚠️ تأكد من استخدام array syntax بالضبط

  const response = await fetchWithAuth(url, {
    method: "POST",
    body: formData,
  }, true); // ✅ نرسل التوكن

  const text = await response.text();
  let data;

  try {
    data = JSON.parse(text);
  } catch (err) {
    console.error("❌ الرد غير صالح:", text);
    throw new Error("⚠️ الرد من السيرفر ليس بصيغة JSON.");
  }

  if (!response.ok) {
    throw new Error(data.message || "❌ فشل في ربط المستخدم بالكاشير.");
  }

  return data;
};

export const removeUserFromCashier = async ({ user_id, cashier_id }) => {
  if (!user_id || !cashier_id) {
    throw new Error("❌ يجب توفير user_id و cashier_id");
  }

  const subdomain = getSubdomain();
  const url = `https://${subdomain}.restocash.app/api/remove/user/cashier?api_password=Project102$_`;

  const formData = new FormData();
  formData.append("cashier_id", cashier_id);
  formData.append("user_ids[]", user_id); // ✅ طبقًا لـ Postman

  const response = await fetchWithAuth(url, {
    method: "POST",
    body: formData,
  }, true); // ✅ يرسل التوكن

  const text = await response.text();
  let data;

  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("❌ فشل في قراءة الرد من السيرفر.");
  }

  if (!response.ok) {
    throw new Error(data?.message || "❌ فشل في حذف الكاشير من المستخدم.");
  }

  return data;
};

// services/Users.js

export const getUserInfo = async (userId) => {
  if (!userId) {
    throw new Error("❗ يجب توفير userId لاسترجاع بيانات المستخدم");
  }

  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/get/user/info/${userId}?api_password=${API_PASSWORD}`;

  const response = await fetchWithAuth(apiUrl, { method: "GET" });

  const text = await response.text();
  console.log("getUserInfo Response Text:", text);

  let data;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error("خطأ في تحليل استجابة السيرفر");
  }

  if (!response.ok) {
    let errorMessage = data?.message || "فشل في جلب بيانات المستخدم";
    if (data?.errors) {
      const fieldErrors = [];
      for (const field in data.errors) {
        const fieldMessages = data.errors[field].join(", ");
        fieldErrors.push(`${field}: ${fieldMessages}`);
      }
      errorMessage += " => " + fieldErrors.join(" | ");
    }
    throw new Error(errorMessage);
  }

  // حاول إرجاع الكائن المستخدم داخل أي مستوى من المستويات المتوقعة
  return data?.data?.data || data?.data || data;
};


export const setUserPermissions = async (userId, permissionsData) => {
  const subdomain = getSubdomain();
  const apiUrl = `https://${subdomain}.restocash.app/api/set/user/permissions?api_password=${API_PASSWORD}`;

  try {
    const response = await fetchWithAuth(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_id: userId,
        ...permissionsData,
      }),
    });

    // نحاول قراءة النص الأصلي أولًا
    const text = await response.text();
    console.log("setUserPermissions Response Text:", text);

    let data;
    try {
      data = JSON.parse(text);
    } catch (err) {
      throw new Error("⚠️ خطأ في تحليل استجابة السيرفر");
    }

    // التحقق من نجاح العملية
    if (!response.ok || !data.status) {
      let errorMessage = data?.message || "❌ فشل في تحديث الصلاحيات";
      if (data?.errors) {
        const fieldErrors = [];
        for (const field in data.errors) {
          const fieldMessages = data.errors[field].join(", ");
          fieldErrors.push(`${field}: ${fieldMessages}`);
        }
        errorMessage += " => " + fieldErrors.join(" | ");
      }
      throw new Error(errorMessage);
    }

    console.log("✅ تم تحديث الصلاحيات بنجاح:", data);
    return data?.data;
  } catch (error) {
    console.error("🚨 خطأ أثناء الاتصال بالـ API:", error);
    throw error;
  }
};